import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Calendar, RefreshCw, X, User, Phone, Mail, MapPin, AlertTriangle, Pill, Clock, CheckCircle, Play, Users } from 'lucide-react';
import { fetchWithAuth, API_URL } from '../utils/api';
import { useLanguage } from './LanguageContext';
import useIsMobile from '../hooks/useIsMobile';
import scaleFormats, { navHeight, compactWeekRange, MOBILE_MAX } from '../utils/schedulerScale';

const Appointments = ({ user, currentBranch }) => {
  const schedulerContainer = useRef();
  const schedulerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const { print_lang, currentLanguage } = useLanguage();
  const isMobile = useIsMobile();

  // Patient info modal state
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  // Today's queue for check-in
  const [todayQueue, setTodayQueue] = useState([]);
  const [showQueuePanel, setShowQueuePanel] = useState(true);

  const branchId = currentBranch?.branch_id;
  const adminId = user?.admin_id;
  const userId = user?.user_id;

  // Store dentist availability for validation
  const dentistAvailabilityRef = useRef([]);

  // Prevent double initialization
  const schedulerInitialized = useRef(false);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/${path}`;
  };

  const fetchPatientDetails = async (patientId) => {
    try {
      const data = await fetchWithAuth(`/api/patients/${patientId}`);
      const patient = data.data || data;

      if (patient && patient.dob) {
        const birthDate = new Date(patient.dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        patient.age = age;
      }

      return patient;
    } catch (error) {
      console.error('Failed to load patient info:', error);
      return null;
    }
  };

  const handleClosePatientModal = () => {
    setShowPatientModal(false);
    setPatientInfo(null);
  };

  // Fetch today's queue
  const fetchTodayQueue = useCallback(async () => {
    if (!branchId) return;
    try {
      const response = await fetchWithAuth(`/api/appointments/today/waiting?branch_id=${branchId}`);
      setTodayQueue(response.data || []);
    } catch (error) {
      console.error('Failed to fetch today queue:', error);
    }
  }, [branchId]);

  // Update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await fetchWithAuth(`/api/appointments/${appointmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      fetchTodayQueue();
      if (window.refreshAppointments) {
        window.refreshAppointments();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(error.message);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    fetchTodayQueue();
    const queueInterval = setInterval(fetchTodayQueue, 30000);
    return () => clearInterval(queueInterval);
  }, [fetchTodayQueue]);

  // EXACT COPY FROM PET-CLINIC
  useEffect(() => {
    const loadScheduler = async () => {
      try {
        // Load CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `/components/codebase/sources/skins/dhtmlxscheduler_material.css`;
        document.head.appendChild(link);

        // Load JS
        const script = document.createElement('script');
        script.src = `/components/codebase/sources/dhtmlxscheduler.js`;
        script.onload = () => {
          initializeScheduler();
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('Error loading scheduler:', error);
        setError('Failed to load scheduler');
        setIsLoading(false);
      }
    };

    loadScheduler();

    return () => {
      if (schedulerRef.current && window.scheduler) {
        try {
          window.scheduler.destructor();
        } catch (e) {
          console.warn('Error destroying scheduler:', e);
        }
      }
      // Reset initialization flag for cleanup
      schedulerInitialized.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default to the day view below 768px, and correct it again on every later
  // rotation. This is deliberately its OWN effect, not a dependency added to
  // the loader effect above: that one's cleanup calls scheduler.destructor()
  // and re-appends the DHTMLX <link>/<script> to <head>, so giving it an
  // isMobile dependency would tear the scheduler down and rebuild it (leaking
  // another pair of tags) on every rotation. Guarded so it no-ops while the
  // scheduler is still loading asynchronously; the initial phone case is
  // handled separately, right after init, inside initializeScheduler.
  useEffect(() => {
    if (!schedulerInitialized.current || !window.scheduler) return;
    // Before setCurrentView, not after: DHTMLX reads nav_height while it lays
    // the view out, and derives the header and grid offsets from it. Set it
    // afterwards and the toolbar grows while the grid stays put, on top of it.
    window.scheduler.xy.nav_height = navHeight(window.innerWidth);
    window.scheduler.setCurrentView(null, isMobile ? 'day' : 'week');
  }, [isMobile]);

  // Update event types when language changes
  useEffect(() => {
    if (window.scheduler && eventTypes.length > 0) {
      window.scheduler.updateCollection("eventTypes", eventTypes.map(et => ({
        key: et.evtype_id,
        label: currentLanguage === 'th' ? (et.type_name_th || et.type_name) : et.type_name
      })));
    }
  }, [currentLanguage, eventTypes]);

  const loadSelectOptions = async () => {
    try {
      const eventTypesData = await fetchWithAuth('/api/appointments/types/all');
      setEventTypes(Array.isArray(eventTypesData) ? eventTypesData : []);

      const patientsData = await fetchWithAuth(
        `/api/appointments/patients/selection?branch_id=${branchId}`
      );

      // Load dentists
      const dentistsData = await fetchWithAuth(
        `/api/appointments/dentists/selection?branch_id=${branchId}`
      );

      return {
        eventTypesData: Array.isArray(eventTypesData) ? eventTypesData : [],
        patientsData: Array.isArray(patientsData) ? patientsData : [],
        staffData: Array.isArray(dentistsData) ? dentistsData : []
      };
    } catch (error) {
      console.error('Error loading select options:', error);
      return { eventTypesData: [], patientsData: [], staffData: [] };
    }
  };

  const initializeScheduler = async () => {
    if (!window.scheduler) {
      console.error('Scheduler not loaded');
      return;
    }

    // Prevent double initialization (React 18 Strict Mode)
    if (schedulerInitialized.current) {
      console.log('Scheduler already initialized, skipping');
      return;
    }
    schedulerInitialized.current = true;

    try {
      const { eventTypesData, patientsData, staffData } = await loadSelectOptions();

      const scheduler = window.scheduler;
      schedulerRef.current = scheduler;

      // Configure scheduler - EXACT COPY FROM PET-CLINIC
      scheduler.config.load_date = "%Y-%m-%d %H:%i";
      scheduler.config.xml_date = "%Y-%m-%d %H:%i";
      scheduler.config.api_date = "%Y-%m-%d %H:%i";
      scheduler.config.readonly = false;
      scheduler.config.details_on_create = true;
      scheduler.config.details_on_dblclick = true;
      scheduler.config.mark_now = true;
      scheduler.config.show_loading = true;
      scheduler.config.multi_day = true;
      scheduler.config.first_hour = 8;
      scheduler.config.last_hour = 20;
      scheduler.config.limit_time_select = true;
      scheduler.config.cascade_event_display = true;
      scheduler.config.cascade_event_count = 4;
      scheduler.config.cascade_event_margin = 30;
      scheduler.config.date_format = "%Y-%m-%d %H:%i";
      scheduler.config.server_utc = false;
      scheduler.config.time_step = 15;

      // Configure lightbox sections
      scheduler.config.lightbox.sections = [
        { name: "description", height: 50, map_to: "text", type: "textarea", focus: true },
        { name: "patient", height: 23, map_to: "patient_id", type: "select", options: scheduler.serverList("patients") },
        { name: "dentist", height: 23, map_to: "dentist_id", type: "select", options: scheduler.serverList("dentists") },
        { name: "type", height: 23, map_to: "evtype_id", type: "select", options: scheduler.serverList("eventTypes") },
        { name: "time", height: 72, type: "time", map_to: "auto" }
      ];

      // Configure labels
      scheduler.locale.labels.section_description = print_lang('description', 'Description');
      scheduler.locale.labels.section_patient = print_lang('patient', 'Patient');
      scheduler.locale.labels.section_dentist = print_lang('dentist', 'Dentist');
      scheduler.locale.labels.section_type = print_lang('type', 'Type');
      scheduler.locale.labels.section_time = print_lang('time', 'Time');

      // Configure buttons with Info button
      scheduler.config.buttons_left = ["dhx_save_btn", "dhx_cancel_btn"];
      scheduler.config.buttons_right = ["dhx_delete_btn", "dhx_custom_btn_info"];

      scheduler.locale.labels["dhx_save_btn"] = "Save";
      scheduler.locale.labels["dhx_cancel_btn"] = "Cancel";
      scheduler.locale.labels["dhx_delete_btn"] = "Delete";
      scheduler.locale.labels["dhx_custom_btn_info"] = "Patient Info";

      // Handle Info button click
      scheduler.attachEvent("onLightboxButton", async function(button_id, node, e) {
        if (button_id === "dhx_custom_btn_info") {
          const eventId = scheduler.getState().lightbox_id;
          const event = scheduler.getEvent(eventId);

          // Get patient_id from lightbox form (for new/unsaved events) or from event object
          let patientId = null;

          // Try to get from lightbox form first
          const patientSection = scheduler.formSection('patient');
          if (patientSection) {
            const selectedValue = patientSection.getValue();
            if (selectedValue) {
              patientId = selectedValue;
            }
          }

          // Fallback to event object
          if (!patientId && event && event.patient_id) {
            patientId = event.patient_id;
          }

          if (patientId) {
            setLoadingPatient(true);
            setShowPatientModal(true);
            try {
              const patientDetails = await fetchPatientDetails(patientId);
              if (patientDetails) {
                setPatientInfo(patientDetails);
              } else {
                alert(print_lang('patient_not_found', 'Patient details not found'));
                setShowPatientModal(false);
              }
            } catch (error) {
              console.error('Error loading patient details:', error);
              alert(print_lang('error_loading_patient', 'Error loading patient details'));
              setShowPatientModal(false);
            } finally {
              setLoadingPatient(false);
            }
          } else {
            alert(print_lang('no_patient_selected', 'No patient selected for this appointment'));
          }
          return false;
        }
      });

      // Set server lists for dropdowns
      scheduler.updateCollection("patients", patientsData.map(p => ({
        key: p.patient_id,
        label: `${p.name} (${p.phone || 'No phone'})`
      })));

      scheduler.updateCollection("dentists", staffData.map(d => ({
        key: d.dentist_id,
        label: d.name
      })));

      scheduler.updateCollection("eventTypes", eventTypesData.map(et => ({
        key: et.evtype_id,
        label: currentLanguage === 'th' ? (et.type_name_th || et.type_name) : et.type_name
      })));

      // Helper function to format dates (needed for dentist availability)
      const formatDate = (date) => {
        if (!date) return null;

        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');

          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }

        if (typeof date === 'string') {
          const parsedDate = new Date(date);
          if (!isNaN(parsedDate.getTime())) {
            const year = parsedDate.getFullYear();
            const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
            const day = String(parsedDate.getDate()).padStart(2, '0');
            const hours = String(parsedDate.getHours()).padStart(2, '0');
            const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
            const seconds = String(parsedDate.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          }
        }

        return date;
      };

      // Initialize scheduler
      // Toolbar height before init, so the header and grid are positioned from
      // the right number on the very first paint rather than jumping once the
      // rotation effect runs. See utils/schedulerScale.js for why a phone needs
      // a taller bar than the library's default.
      scheduler.xy.nav_height = navHeight(window.innerWidth);

      scheduler.init(schedulerContainer.current, new Date(), "week");

      // Fix z-index for DHTMLX modal dialogs (delete confirmation)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Check for modal cover (background overlay)
              if (node.classList && node.classList.contains('dhx_modal_cover')) {
                node.style.cssText = 'z-index: 99998 !important; position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; background-color: rgba(0, 0, 0, 0.4) !important; pointer-events: auto !important;';
              }
              // Check for modal box (dialog)
              if (node.classList && node.classList.contains('dhx_modal_box')) {
                node.style.cssText = 'z-index: 99999 !important; position: fixed !important; top: 50% !important; left: 50% !important; transform: translate(-50%, -50%) !important; background: white !important; border-radius: 4px !important; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important; pointer-events: auto !important; min-width: 300px !important;';

                // Disable pointer events on lightbox while modal is open
                const lightbox = document.querySelector('.dhx_cal_light');
                if (lightbox) {
                  lightbox.style.pointerEvents = 'none';
                }

                // Also fix child elements to ensure they're clickable
                const buttons = node.querySelectorAll('.dhx_popup_button, div[class*="button"]');
                buttons.forEach(btn => {
                  btn.style.pointerEvents = 'auto';
                  btn.style.cursor = 'pointer';
                });

                // Ensure all children have pointer-events
                const allChildren = node.querySelectorAll('*');
                allChildren.forEach(child => {
                  child.style.pointerEvents = 'auto';
                });
              }
            }
          });

          // When modal is removed, re-enable lightbox pointer events
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList && node.classList.contains('dhx_modal_box')) {
              const lightbox = document.querySelector('.dhx_cal_light');
              if (lightbox) {
                lightbox.style.pointerEvents = 'auto';
              }
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Listen for lightbox open to check dentist day-off (after init)
      scheduler.attachEvent("onLightbox", function(id) {
        // Use setTimeout to not block lightbox opening
        setTimeout(async () => {
          const event = scheduler.getEvent(id);
          if (event && event.start_date && event.end_date) {
            try {
              const start = formatDate(event.start_date);
              const end = formatDate(event.end_date);

              // Check dentist day-off only
              const response = await fetchWithAuth(
                `/api/appointments/dentists/available?branch_id=${branchId}&start_date=${start}&end_date=${end}`
              );

              if (response.success && response.data) {
                // Store availability data for validation (dentists only)
                dentistAvailabilityRef.current = response.data;

                // Update dropdown styling for dentists with day-off
                const dentistSelect = document.querySelector('.dhx_cal_light select[name="dentist_id"]');
                if (dentistSelect) {
                  const options = dentistSelect.querySelectorAll('option');
                  options.forEach((option) => {
                    // Find dentist by dentist_id
                    const dentist = response.data.find(d =>
                      String(d.dentist_id) === option.value
                    );

                    // Only mark dentists with day-off
                    if (dentist && dentist.has_dayoff) {
                      option.style.color = '#ef4444';
                      option.style.fontStyle = 'italic';
                      // Preserve original name and add day-off indicator
                      if (!option.textContent.includes('(Day Off)')) {
                        option.textContent = `${dentist.name} (Day Off)`;
                      }
                    }
                  });
                }
              }
            } catch (error) {
              console.error('Error checking dentist availability:', error);
            }
          }
        }, 200);
        return true;
      })

      // Load events function
      const loadEvents = async () => {
        setIsLoading(true);
        const url = `/api/appointments?branch_id=${branchId}&admin_id=${adminId}`;

        try {
          const data = await fetchWithAuth(url);
          scheduler.clearAll();

          const eventsArray = Array.isArray(data) ? data : [];

          const formattedData = eventsArray.map(event => {
            const startDate = new Date(event.start_date + (event.start_date.includes('T') ? '' : 'T00:00:00'));
            const endDate = new Date(event.end_date + (event.end_date.includes('T') ? '' : 'T00:00:00'));

            return {
              ...event,
              start_date: startDate,
              end_date: endDate
            };
          });

          scheduler.parse(formattedData, "json");
          setIsLoading(false);
        } catch (error) {
          console.error('Error loading events:', error);
          setError('Failed to load appointments: ' + error.message);
          setIsLoading(false);
        }
      };

      // Override DHTMLX confirm to use browser's native confirm dialog
      // This avoids CSS conflicts with the built-in modal
      if (window.dhtmlx) {
        window.dhtmlx.confirm = function(config) {
          const message = typeof config === 'string' ? config : (config.text || 'Are you sure?');
          if (window.confirm(message)) {
            if (config.callback) config.callback(true);
          } else {
            if (config.callback) config.callback(false);
          }
        };
      }

      // Also override scheduler's confirm if it exists
      if (scheduler.confirm) {
        scheduler._original_confirm = scheduler.confirm;
        scheduler.confirm = function(config) {
          const message = typeof config === 'string' ? config : (config.text || 'Are you sure?');
          if (window.confirm(message)) {
            if (config.callback) config.callback(true);
          } else {
            if (config.callback) config.callback(false);
          }
        };
      }

      // Before event save - validate and check dentist availability
      scheduler.attachEvent("onEventSave", function(id, event, is_new) {
        event.branch_id = branchId;
        event.admin_id = adminId;
        event.user_id = userId;

        if (!event.text || event.text.trim() === '') {
          alert(print_lang('enter_description', 'Please enter a description'));
          return false;
        }

        // Check if selected dentist has day-off using cached availability data
        if (event.dentist_id && dentistAvailabilityRef.current.length > 0) {
          const selectedDentist = dentistAvailabilityRef.current.find(
            d => d.dentist_id === parseInt(event.dentist_id)
          );
          if (selectedDentist && selectedDentist.has_dayoff) {
            alert(print_lang('dentist_has_dayoff', `${selectedDentist.name} has a day off during this time period. Please select another dentist.`));
            return false;
          }
        }

        return true;
      });

      // Create data processor
      scheduler.createDataProcessor(async function(entity, action, data, id) {
        const baseUrl = "/api/appointments";
        let url = baseUrl;
        let method = 'GET';
        let body = null;

        // Check if ID is a temporary ID (timestamp - very large number)
        const isTemporaryId = id && (typeof id === 'number' ? id > 1000000000000 : parseInt(id) > 1000000000000);

        switch(action) {
          case "create":
            method = 'POST';
            body = JSON.stringify({
              start_date: formatDate(data.start_date),
              end_date: formatDate(data.end_date),
              text: data.text,
              branch_id: branchId,
              admin_id: adminId,
              user_id: userId,
              patient_id: data.patient_id || null,
              dentist_id: data.dentist_id || null,
              evtype_id: data.evtype_id || null
            });
            break;

          case "update":
            if (isTemporaryId) {
              return { tid: id };
            }
            url = `${baseUrl}/${id}`;
            method = 'PUT';
            body = JSON.stringify({
              start_date: formatDate(data.start_date),
              end_date: formatDate(data.end_date),
              text: data.text,
              branch_id: branchId,
              admin_id: adminId,
              user_id: userId,
              patient_id: data.patient_id || null,
              dentist_id: data.dentist_id || null,
              evtype_id: data.evtype_id || null
            });
            break;

          case "delete":
            if (isTemporaryId) {
              return { tid: id };
            }
            url = `${baseUrl}/${id}`;
            method = 'DELETE';
            break;
          default:
            break;
        }

        try {
          const result = await fetchWithAuth(url, {
            method: method,
            body: body
          });

          // For create, return the new ID to replace temporary ID
          // API returns { action: 'inserted', tid: newId }
          if (action === "create") {
            const newId = result.tid || result.id || result.insertId;
            console.log('Created event with ID:', newId, 'replacing temp ID:', id);
            return { tid: newId, action: "inserted" };
          }

          return { tid: id, action: action === "delete" ? "deleted" : "updated" };
        } catch (error) {
          console.error('API error:', error);
          alert('Error saving appointment: ' + error.message);
          return { tid: id, action: "error" };
        }
      });

      // Event templates
      scheduler.templates.event_class = function(start, end, event) {
        let css = 'custom_event';
        if (event.status === 'checked_in') css += ' event-checked-in';
        if (event.status === 'in_progress') css += ' event-in-progress';
        if (event.status === 'completed') css += ' event-completed';
        if (event.status === 'cancelled') css += ' event-cancelled';
        return css;
      };

      scheduler.templates.event_text = function(start, end, event) {
        let text = event.text || '';
        if (event.patient_name) {
          text += `<br><small>${event.patient_name}</small>`;
        }
        return text;
      };

      // Column headers. DHTMLX divides the container into equal scale cells and
      // never wraps or ellipsises their labels, so a label too long for its cell
      // spills onto the next one: at 390px "Mon, September 14" needs 69px in a
      // 46px week column, and "Wednesday" needs 72px in a 56px month column.
      // Phones get the abbreviated day name; desktop keeps the long form, which
      // is what these defaults already were. See utils/schedulerScale.js.
      // Read the width live rather than the captured `isMobile`: DHTMLX calls
      // these on every render, and there are three tiers here, not two - the
      // narrowest phones cannot fit the date number at any readable size.
      scheduler.templates.week_scale_date = function(date) {
        return scheduler.date.date_to_str(scaleFormats(window.innerWidth).week)(date);
      };
      scheduler.templates.month_scale_date = function(date) {
        return scheduler.date.date_to_str(scaleFormats(window.innerWidth).month)(date);
      };

      // The week range in the toolbar. "14 Sep 2026 – 20 Sep 2026" needs 184px
      // and the phone row leaves 136px at 320px, so it was cut off on a small
      // phone. Desktop delegates to the library's own template, captured here
      // rather than reimplemented, so the wide layout keeps exactly the text it
      // has today.
      const defaultWeekDate = scheduler.templates.week_date;
      scheduler.templates.week_date = function(start, end) {
        if (window.innerWidth > MOBILE_MAX) return defaultWeekDate(start, end);
        // `end` is exclusive — the Monday after the week — so the label would
        // otherwise name a day that is not on screen.
        const last = scheduler.date.add(end, -1, 'day');
        return compactWeekRange(start, last, scheduler.date.date_to_str);
      };

      // Load initial events
      loadEvents();

      // scheduler.init() above always starts on "week". Correct it immediately
      // when the page loaded on a phone; a later rotation is caught by the
      // separate [isMobile] effect, since this closure's `isMobile` is frozen
      // to whatever it was when the async load finished.
      if (isMobile) {
        scheduler.setCurrentView(null, 'day');
      }

      // Store refresh function
      window.refreshAppointments = loadEvents;

      console.log('Scheduler initialized successfully');

    } catch (error) {
      console.error('Error initializing scheduler:', error);
      setError('Failed to initialize scheduler: ' + error.message);
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (window.refreshAppointments) {
      window.refreshAppointments();
    }
  };

  const handleToday = () => {
    if (schedulerRef.current) {
      schedulerRef.current.setCurrentView(new Date(), "day");
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-red-500 mb-4">
          <Calendar size={48} />
        </div>
        <h3 className="text-xl font-medium text-gray-700 mb-2">{print_lang('error_loading', 'Error Loading Appointments')}</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {print_lang('reload', 'Reload Page')}
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header Controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar size={24} />
            {print_lang('appointments', 'Appointments')}
          </h2>
          {currentBranch && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              {currentBranch.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleToday}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            {print_lang('today', 'Today')}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            {print_lang('refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && !schedulerRef.current && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center" style={{ zIndex: 100 }}>
          <div className="flex items-center gap-3">
            <RefreshCw size={24} className="animate-spin text-blue-600" />
            <span className="text-gray-700">{print_lang('loading', 'Loading...')}</span>
          </div>
        </div>
      )}

      {/* Scheduler Container - EXACT STRUCTURE FROM PET-CLINIC */}
      <div className="flex-1 relative bg-white rounded-lg shadow-sm border">
        <div
          ref={schedulerContainer}
          className="dhx_cal_container"
          style={{ width: '100%', height: '600px' }}
        >
          <div className="dhx_cal_navline">
            <div className="dhx_cal_prev_button">&nbsp;</div>
            <div className="dhx_cal_next_button">&nbsp;</div>
            <div className="dhx_cal_today_button"></div>
            <div className="dhx_cal_date"></div>
            <div className="dhx_cal_tab" name="day_tab"></div>
            <div className="dhx_cal_tab" name="week_tab"></div>
            <div className="dhx_cal_tab" name="month_tab"></div>
          </div>
          <div className="dhx_cal_header"></div>
          <div className="dhx_cal_data"></div>
        </div>
      </div>

      {/* Today's Queue Panel */}
      <div className="mt-4">
        <div
          className="flex items-center justify-between p-3 bg-gray-100 rounded-t-lg cursor-pointer hover:bg-gray-200"
          onClick={() => setShowQueuePanel(!showQueuePanel)}
        >
          <h3 className="font-medium flex items-center gap-2">
            <Users size={18} />
            {print_lang('todays_queue', "Today's Queue")}
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-sm">
              {todayQueue.length}
            </span>
          </h3>
          <span className="text-gray-500">{showQueuePanel ? '▼' : '▶'}</span>
        </div>

        {showQueuePanel && (
          <div className="border border-t-0 rounded-b-lg bg-white">
            {todayQueue.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Calendar size={32} className="mx-auto mb-2 text-gray-300" />
                <p>{print_lang('no_appointments_today', 'No appointments today')}</p>
              </div>
            ) : (
              <div className="divide-y max-h-64 overflow-y-auto">
                {todayQueue.map((apt) => (
                  <div key={apt.id} className="p-3 hover:bg-gray-50 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User size={20} className="text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium">{apt.patient_name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock size={12} />
                          {formatTime(apt.start_date)}
                          {apt.staff_name && (
                            <span className="text-blue-600">• {apt.staff_name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {apt.drug_allergy && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1">
                          <AlertTriangle size={12} />
                          {print_lang('allergy', 'Allergy')}
                        </span>
                      )}

                      {apt.status === 'scheduled' && (
                        <>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                            {print_lang('scheduled', 'Scheduled')}
                          </span>
                          <button
                            onClick={() => updateAppointmentStatus(apt.id, 'checked_in')}
                            className="btn btn-primary btn-sm flex items-center gap-1"
                          >
                            <CheckCircle size={14} />
                            {print_lang('check_in', 'Check In')}
                          </button>
                        </>
                      )}

                      {apt.status === 'checked_in' && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1">
                          <Clock size={12} />
                          {print_lang('waiting', 'Waiting')}
                        </span>
                      )}

                      {apt.status === 'in_progress' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
                          <Play size={12} />
                          {print_lang('in_progress', 'In Progress')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {eventTypes.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">{print_lang('event_types', 'Event Types')}:</h3>
          <div className="flex flex-wrap gap-4">
            {eventTypes.map(type => (
              <div key={type.evtype_id} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: type.color }}
                ></div>
                <span className="text-sm text-gray-600">
                  {currentLanguage === 'th' ? (type.type_name_th || type.type_name) : type.type_name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patient Info Modal */}
      {showPatientModal && (
        <div className="modal-overlay" onClick={handleClosePatientModal}>
          <div className="modal-content w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <User size={24} />
                {print_lang('patient_info', 'Patient Information')}
              </h2>
              <button onClick={handleClosePatientModal}>
                <X size={24} />
              </button>
            </div>

            {loadingPatient ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : patientInfo ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  {patientInfo.images_path ? (
                    <img
                      src={getImageUrl(patientInfo.images_path)}
                      alt={patientInfo.name}
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                      <User size={32} className="text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold">
                      {patientInfo.prefix} {patientInfo.name}
                    </h3>
                    {patientInfo.name_en && (
                      <p className="text-gray-500">{patientInfo.name_en}</p>
                    )}
                    <p className="text-sm text-gray-400 font-mono">{patientInfo.citizen_id || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    <span>{patientInfo.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-gray-400" />
                    <span>{patientInfo.email || '-'}</span>
                  </div>
                </div>

                {patientInfo.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{patientInfo.address}</span>
                  </div>
                )}

                {(patientInfo.medical_history || patientInfo.drug_allergy) && (
                  <div className="border-t pt-4 space-y-3">
                    {patientInfo.drug_allergy && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                          <Pill size={16} />
                          {print_lang('drug_allergy', 'Drug Allergy')}
                        </div>
                        <p className="text-red-600 text-sm">{patientInfo.drug_allergy}</p>
                      </div>
                    )}
                    {patientInfo.medical_history && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-orange-700 font-medium mb-1">
                          <AlertTriangle size={16} />
                          {print_lang('medical_history', 'Medical History')}
                        </div>
                        <p className="text-orange-600 text-sm">{patientInfo.medical_history}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t pt-4 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <label className="text-gray-400 block">{print_lang('gender', 'Gender')}</label>
                    <span className="font-medium">{print_lang(patientInfo.gender, patientInfo.gender)}</span>
                  </div>
                  <div>
                    <label className="text-gray-400 block">{print_lang('blood_group', 'Blood')}</label>
                    <span className="font-medium text-red-600">{patientInfo.blood_group || '-'}</span>
                  </div>
                  <div>
                    <label className="text-gray-400 block">{print_lang('age', 'Age')}</label>
                    <span className="font-medium">{patientInfo.age || '-'}</span>
                  </div>
                </div>

                {patientInfo.emergency_contact && (
                  <div className="border-t pt-4 text-sm">
                    <label className="text-gray-400 block mb-1">{print_lang('emergency_contact', 'Emergency Contact')}</label>
                    <span className="font-medium">{patientInfo.emergency_contact}</span>
                    {patientInfo.emergency_phone && (
                      <span className="ml-2 text-gray-500">({patientInfo.emergency_phone})</span>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={handleClosePatientModal}
                className="btn btn-secondary"
              >
                {print_lang('close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .event-checked-in { background-color: #f59e0b !important; }
        .event-in-progress { background-color: #8b5cf6 !important; }
        .event-completed { background-color: #10b981 !important; }
        .event-cancelled { background-color: #ef4444 !important; opacity: 0.6; }
      `}</style>
    </div>
  );
};

export default Appointments;
